'use client';

import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/use-locale';
import { useSession } from '@/hooks/use-session';
import { useTurnstile } from '@/hooks/use-turnstile';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ContactSalesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SUBJECT = 'Enterprise Plan Inquiry';

export function ContactSalesDialog({ open, onOpenChange }: ContactSalesDialogProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { data: session } = useSession();
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';
  const { token: turnstileToken, reset: resetTurnstile, containerRef } =
    useTurnstile(siteKey, open);

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Whether the user has typed in the email field since the dialog opened.
  // Guards the prefill: it may fill an untouched empty field (including when
  // the session resolves a beat after open), but must never overwrite or
  // refill what the user typed or deliberately cleared.
  const [emailTouched, setEmailTouched] = useState(false);

  useEffect(() => {
    if (open && !emailTouched && !email && session?.user?.email) {
      setEmail(session.user.email);
    }
    // `email` is deliberately NOT a dependency: with it, clearing the field
    // re-ran the effect and instantly refilled the session email — the user
    // could never enter a different address.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session?.user?.email, emailTouched]);

  useEffect(() => {
    if (!open) {
      setError(null);
      setSuccess(false);
      setEmailTouched(false);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (siteKey && !turnstileToken) {
      setError(t('pages.plans.contactSales.errors.turnstileRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: session?.user?.name || email,
          email,
          subject: SUBJECT,
          message,
          turnstileToken: turnstileToken || '',
          language: locale,
        }),
      });

      if (response.ok) {
        setSuccess(true);
        setMessage('');
      } else {
        setError(t('pages.plans.contactSales.errors.sendFailed'));
      }
    } catch {
      setError(t('pages.plans.contactSales.errors.sendFailed'));
    } finally {
      setIsSubmitting(false);
      resetTurnstile();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="fd-diagram border-0 p-7"
        style={{ background: 'var(--fd-card)', color: 'var(--fd-ink)' }}
      >
        <DialogHeader>
          <DialogTitle
            className="font-semibold"
            style={{ color: 'var(--fd-ink)', letterSpacing: '-0.015em' }}
          >
            {t('pages.plans.contactSales.title')}
          </DialogTitle>
          <DialogDescription style={{ color: 'var(--fd-ink-2)' }}>
            {t('pages.plans.contactSales.description')}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div
            className="flex flex-col items-start gap-3 rounded-lg p-4"
            style={{
              background: 'var(--fd-bg)',
              border: '1px solid var(--fd-line)',
            }}
          >
            <div
              className="flex items-center gap-2"
              style={{ color: 'var(--fd-good)' }}
            >
              <CheckCircle2 className="size-5" aria-hidden />
              <span className="text-sm font-semibold">
                {t('pages.plans.contactSales.successTitle')}
              </span>
            </div>
            <p
              className="m-0 text-[13.5px] leading-relaxed"
              style={{ color: 'var(--fd-ink-2)' }}
            >
              {t('pages.plans.contactSales.successDescription')}
            </p>
            <DialogClose
              className="mt-1 rounded-[10px] px-4 py-2 text-sm font-semibold"
              style={{
                background: 'var(--fd-ink)',
                color: 'var(--fd-card)',
                border: 'none',
              }}
            >
              {t('pages.plans.contactSales.close')}
            </DialogClose>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="contact-sales-email"
                className="fd-mono text-[11px] uppercase"
                style={{
                  color: 'var(--fd-muted)',
                  letterSpacing: '0.12em',
                }}
              >
                {t('pages.plans.contactSales.emailLabel')}
              </label>
              <input
                id="contact-sales-email"
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmailTouched(true);
                  setEmail(e.target.value);
                }}
                disabled={isSubmitting}
                placeholder={t('pages.plans.contactSales.emailPlaceholder')}
                className="w-full rounded-[10px] px-3 py-2.5 text-sm outline-none transition-colors disabled:opacity-60"
                style={{
                  background: 'var(--fd-bg)',
                  color: 'var(--fd-ink)',
                  border: '1px solid var(--fd-line)',
                }}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="contact-sales-message"
                className="fd-mono text-[11px] uppercase"
                style={{
                  color: 'var(--fd-muted)',
                  letterSpacing: '0.12em',
                }}
              >
                {t('pages.plans.contactSales.messageLabel')}
              </label>
              <textarea
                id="contact-sales-message"
                required
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSubmitting}
                placeholder={t('pages.plans.contactSales.messagePlaceholder')}
                className="w-full resize-y rounded-[10px] px-3 py-2.5 text-sm outline-none transition-colors disabled:opacity-60"
                style={{
                  background: 'var(--fd-bg)',
                  color: 'var(--fd-ink)',
                  border: '1px solid var(--fd-line)',
                  minHeight: '120px',
                }}
              />
            </div>

            {siteKey && <div ref={containerRef} />}

            {error && (
              <div
                className="rounded-lg px-3 py-2 text-[13px]"
                style={{
                  background: 'var(--fd-bg)',
                  color: 'var(--fd-bad, #b91c1c)',
                  border: '1px solid var(--fd-line)',
                }}
              >
                {error}
              </div>
            )}

            <div className="mt-1 flex items-center justify-end gap-2">
              <DialogClose
                type="button"
                disabled={isSubmitting}
                className="rounded-[10px] px-4 py-2 text-sm font-semibold transition-opacity disabled:opacity-60"
                style={{
                  background: 'transparent',
                  color: 'var(--fd-ink-2)',
                  border: '1px solid var(--fd-line)',
                }}
              >
                {t('pages.plans.contactSales.cancel')}
              </DialogClose>
              <button
                type="submit"
                disabled={isSubmitting || (!!siteKey && !turnstileToken)}
                className="inline-flex items-center justify-center gap-2 rounded-[10px] px-4 py-2 text-sm font-semibold transition-opacity disabled:opacity-60"
                style={{
                  background: 'var(--fd-ink)',
                  color: 'var(--fd-card)',
                  border: 'none',
                }}
              >
                {isSubmitting && (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                )}
                {isSubmitting
                  ? t('pages.plans.contactSales.submitting')
                  : t('pages.plans.contactSales.submit')}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
